import { useState, useEffect } from "react";
import { FaTimes, FaPrint } from "react-icons/fa";

export default function MainStorePDF({ isOpen, onClose, data, filters }) {
  const [printData, setPrintData] = useState([]);
  const [summary, setSummary] = useState({
    totalProducts: 0,
    totalValue: 0,
    lowStockCount: 0,
    activeSuppliersCount: 0,
  });

  useEffect(() => {
    if (isOpen && data) {
      setPrintData(data);
      calculateSummary(data);
    }
  }, [isOpen, data]);

  // Test jsPDF availability on component mount
  useEffect(() => {
    console.log("MainStorePDF component mounted");
  }, []);

  function calculateSummary(data) {
    const totalProducts = data.length;
    const totalValue = data.reduce(
      (sum, item) => sum + (item.totalValue || 0),
      0
    );
    const lowStockCount = data.filter((item) => item.totalQty < 10).length;
    const activeSuppliersCount = new Set(
      data.flatMap((item) => item.suppliers || [])
    ).size;

    setSummary({
      totalProducts,
      totalValue,
      lowStockCount,
      activeSuppliersCount,
    });
  }

  function getStockStatus(item) {
    if (item.totalQty < 10) {
      return { text: "Low Stock", class: "bg-red-100 text-red-800" };
    } else if (item.totalQty > 100) {
      return { text: "Over Stock", class: "bg-yellow-100 text-yellow-800" };
    } else {
      return { text: "In Stock", class: "bg-green-100 text-green-800" };
    }
  }

  function formatDate(dateString) {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString();
    } catch (error) {
      return "N/A";
    }
  }

  function formatFilters() {
    const activeFilters = [];
    if (filters?.searchTerm)
      activeFilters.push(`Search: ${filters.searchTerm}`);
    if (filters?.selectedSupplier)
      activeFilters.push(`Supplier: ${filters.selectedSupplier}`);
    if (filters?.startDate && filters?.endDate) {
      activeFilters.push(
        `Date Range: ${filters.startDate} to ${filters.endDate}`
      );
    }
    if (filters?.stockLevel)
      activeFilters.push(`Stock Level: ${filters.stockLevel}`);
    if (filters?.sortBy) activeFilters.push(`Sorted by: ${filters.sortBy}`);

    return activeFilters.length > 0
      ? activeFilters.join(", ")
      : "No filters applied";
  }

  function handlePrint() {
    try {
      // Create a new window for printing
      const printWindow = window.open("", "_blank");

      // Get the content to print
      const contentToPrint = document.querySelector(".print-content");

      if (!contentToPrint) {
        alert("Print content not found. Please try again.");
        return;
      }

      // Create the print HTML
      const printHTML = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Main Store Report</title>
          <style>
            @media print {
              body {
                margin: 0;
                padding: 20px;
                font-family: Arial, sans-serif;
                font-size: 12px;
                line-height: 1.4;
                color: black;
                background: white;
              }
              
              .print-header {
                text-align: center;
                margin-bottom: 20px;
                border-bottom: 2px solid #333;
                padding-bottom: 10px;
              }
              
              .print-title {
                font-size: 24px;
                font-weight: bold;
                margin-bottom: 5px;
              }
              
              .print-date {
                font-size: 14px;
                color: #666;
              }
              
              .print-summary {
                margin-bottom: 20px;
                padding: 15px;
                background: #f5f5f5;
                border-radius: 5px;
              }
              
              .print-summary h3 {
                margin: 0 0 10px 0;
                font-size: 16px;
                font-weight: bold;
              }
              
              .print-summary-grid {
                display: grid;
                grid-template-columns: repeat(4, 1fr);
                gap: 15px;
              }
              
              .print-summary-item {
                text-align: center;
                padding: 10px;
                background: white;
                border-radius: 5px;
                border: 1px solid #ddd;
              }
              
              .print-summary-label {
                font-size: 10px;
                color: #666;
                margin-bottom: 5px;
              }
              
              .print-summary-value {
                font-size: 18px;
                font-weight: bold;
                color: #333;
              }
              
              .print-table {
                width: 100%;
                border-collapse: collapse;
                margin-top: 20px;
                font-size: 10px;
              }
              
              .print-table th,
              .print-table td {
                border: 1px solid #ddd;
                padding: 6px;
                text-align: left;
              }
              
              .print-table th {
                background: #f0f0f0;
                font-weight: bold;
                font-size: 9px;
              }
              
              .print-table td {
                font-size: 9px;
              }
              
              .print-status {
                padding: 2px 6px;
                border-radius: 3px;
                font-size: 8px;
                font-weight: bold;
              }
              
              .status-low {
                background: #fee;
                color: #c00;
              }
              
              .status-over {
                background: #fef;
                color: #a0a;
              }
              
              .status-in {
                background: #efe;
                color: #0a0;
              }
              
              .print-footer {
                margin-top: 20px;
                text-align: center;
                font-size: 10px;
                color: #666;
                border-top: 1px solid #ddd;
                padding-top: 10px;
              }
              
              @page {
                size: A4;
                margin: 15mm;
              }
            }
          </style>
        </head>
        <body>
          <div class="print-header">
            <div class="print-title">Main Store Report</div>
            <div class="print-date">Generated on ${new Date().toLocaleString()}</div>
          </div>
          
          <div class="print-summary">
            <h3>Summary</h3>
            <div class="print-summary-grid">
              <div class="print-summary-item">
                <div class="print-summary-label">Total Products</div>
                <div class="print-summary-value">${summary.totalProducts}</div>
              </div>
              <div class="print-summary-item">
                <div class="print-summary-label">Total Value</div>
                <div class="print-summary-value">₹${summary.totalValue.toLocaleString()}</div>
              </div>
              <div class="print-summary-item">
                <div class="print-summary-label">Low Stock Items</div>
                <div class="print-summary-value">${summary.lowStockCount}</div>
              </div>
              <div class="print-summary-item">
                <div class="print-summary-label">Active Suppliers</div>
                <div class="print-summary-value">${
                  summary.activeSuppliersCount
                }</div>
              </div>
            </div>
          </div>
          
          <table class="print-table">
            <thead>
              <tr>
                <th>SNo</th>
                <th>Prod ID</th>
                <th>Product Name</th>
                <th>UOM</th>
                <th>Opening Stock</th>
                <th>Purchased Qty</th>
                <th>Total Qty</th>
                <th>Avg Rate</th>
                <th>Total Value</th>
                <th>Suppliers</th>
                <th>Last Purchase</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              ${printData
                .map((item, idx) => {
                  const status = getStockStatus(item);
                  return `
                  <tr>
                    <td>${idx + 1}</td>
                    <td>${item.prodId || "N/A"}</td>
                    <td>${item.prodName || "N/A"}</td>
                    <td>${item.uom || "N/A"}</td>
                    <td>${item.openingStockQty || 0}</td>
                    <td>${item.inventoryQty || 0}</td>
                    <td>${item.totalQty || 0}</td>
                    <td>₹${(item.avgRate || 0).toFixed(2)}</td>
                    <td>₹${(item.totalValue || 0).toLocaleString()}</td>
                    <td>${(item.suppliers || []).join(", ") || "N/A"}</td>
                    <td>${formatDate(item.lastPurchaseDate)}</td>
                    <td><span class="print-status status-${status.text
                      .toLowerCase()
                      .replace(" ", "-")}">${status.text}</span></td>
                  </tr>
                `;
                })
                .join("")}
            </tbody>
          </table>
          
          <div class="print-footer">
            <p>Report generated on ${new Date().toLocaleString()}</p>
            <p>Total records: ${printData.length}</p>
          </div>
        </body>
        </html>
      `;

      // Write the content to the new window
      printWindow.document.write(printHTML);
      printWindow.document.close();

      // Wait for content to load, then print
      printWindow.onload = function () {
        printWindow.print();
        printWindow.close();
      };
    } catch (error) {
      console.error("Print error:", error);
      alert("Failed to print. Please try again.");
    }
  }

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">
              Main Store Report
            </h2>
            <p className="text-gray-600 mt-1">
              Generated on {new Date().toLocaleString()}
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handlePrint}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              <FaPrint /> Print
            </button>
            <button
              onClick={onClose}
              className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              <FaTimes /> Close
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)] print-content">
          {/* Report Info */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              Report Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <p>
                  <strong>Filters Applied:</strong> {formatFilters()}
                </p>
                <p>
                  <strong>Total Items:</strong> {summary.totalProducts}
                </p>
              </div>
              <div>
                <p>
                  <strong>Total Value:</strong> ₹
                  {summary.totalValue.toLocaleString()}
                </p>
                <p>
                  <strong>Low Stock Items:</strong> {summary.lowStockCount}
                </p>
              </div>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-600">Total Products</p>
              <p className="text-2xl font-bold text-blue-800">
                {summary.totalProducts}
              </p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <p className="text-sm text-green-600">Total Value</p>
              <p className="text-2xl font-bold text-green-800">
                ₹{summary.totalValue.toLocaleString()}
              </p>
            </div>
            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
              <p className="text-sm text-yellow-600">Low Stock Items</p>
              <p className="text-2xl font-bold text-yellow-800">
                {summary.lowStockCount}
              </p>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <p className="text-sm text-purple-600">Active Suppliers</p>
              <p className="text-2xl font-bold text-purple-800">
                {summary.activeSuppliersCount}
              </p>
            </div>
          </div>

          {/* Data Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full border border-gray-300">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">
                    SNo
                  </th>
                  <th className="border border-gray-300 px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">
                    Prod ID
                  </th>
                  <th className="border border-gray-300 px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">
                    Product Name
                  </th>
                  <th className="border border-gray-300 px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">
                    UOM
                  </th>
                  <th className="border border-gray-300 px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">
                    Opening Stock
                  </th>
                  <th className="border border-gray-300 px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">
                    Purchased Qty
                  </th>
                  <th className="border border-gray-300 px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">
                    Total Qty
                  </th>
                  <th className="border border-gray-300 px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">
                    Avg Rate
                  </th>
                  <th className="border border-gray-300 px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">
                    Total Value
                  </th>
                  <th className="border border-gray-300 px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">
                    Suppliers
                  </th>
                  <th className="border border-gray-300 px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">
                    Last Purchase
                  </th>
                  <th className="border border-gray-300 px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {printData.length === 0 ? (
                  <tr>
                    <td
                      colSpan={12}
                      className="border border-gray-300 px-3 py-4 text-center text-gray-500"
                    >
                      No data available for printing
                    </td>
                  </tr>
                ) : (
                  printData.map((item, idx) => {
                    const status = getStockStatus(item);
                    return (
                      <tr key={item.prodId || idx} className="hover:bg-gray-50">
                        <td className="border border-gray-300 px-3 py-2 text-sm text-gray-900">
                          {idx + 1}
                        </td>
                        <td className="border border-gray-300 px-3 py-2 text-sm font-medium text-gray-900">
                          {item.prodId || "N/A"}
                        </td>
                        <td className="border border-gray-300 px-3 py-2 text-sm text-gray-900">
                          {item.prodName || "N/A"}
                        </td>
                        <td className="border border-gray-300 px-3 py-2 text-sm text-gray-700">
                          {item.uom || "N/A"}
                        </td>
                        <td className="border border-gray-300 px-3 py-2 text-sm text-gray-700">
                          {item.openingStockQty || 0}
                        </td>
                        <td className="border border-gray-300 px-3 py-2 text-sm text-gray-700">
                          {item.inventoryQty || 0}
                        </td>
                        <td className="border border-gray-300 px-3 py-2 text-sm font-bold text-gray-900">
                          {item.totalQty || 0}
                        </td>
                        <td className="border border-gray-300 px-3 py-2 text-sm text-gray-700">
                          ₹{(item.avgRate || 0).toFixed(2)}
                        </td>
                        <td className="border border-gray-300 px-3 py-2 text-sm font-bold text-gray-900">
                          ₹{(item.totalValue || 0).toLocaleString()}
                        </td>
                        <td className="border border-gray-300 px-3 py-2 text-sm text-gray-700">
                          {(item.suppliers || []).join(", ") || "N/A"}
                        </td>
                        <td className="border border-gray-300 px-3 py-2 text-sm text-gray-700">
                          {formatDate(item.lastPurchaseDate)}
                        </td>
                        <td className="border border-gray-300 px-3 py-2">
                          <span
                            className={`px-2 py-1 text-xs font-medium rounded-full ${status.class}`}
                          >
                            {status.text}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Footer */}
          <div className="mt-6 pt-4 border-t border-gray-200 text-sm text-gray-600">
            <p>Report generated on {new Date().toLocaleString()}</p>
            <p>Total records: {printData.length}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
